import { PageHead } from '../../components/ui/PageHead';
import { HomepageHero } from '../../components/hero/HomepageHero';
import { StatsBar } from '../../components/content/StatsBar';
import { Section } from '../../components/layout/Section';
import { Container } from '../../components/layout/Container';
import { Grid } from '../../components/layout/Grid';
import { Stack } from '../../components/layout/Stack';
import { PillarCard } from '../../components/cards/PillarCard';
import { IndustryCard } from '../../components/cards/IndustryCard';
import { ProcessSteps } from '../../components/content/ProcessSteps';
import { TechLogoGrid } from '../../components/content/TechLogoGrid';
import { CTABand } from '../../components/cta/CTABand';
import styles from './HomePage.module.css';

const pillars = [
  {
    icon: '🏛️',
    title: 'Medallion Lakehouse',
    description:
      'Cloud-agnostic Bronze, Silver, Gold architecture with embedded data quality, lineage, and governance baked in at every layer.',
    points: ['Delta Lake & Iceberg support', 'Automated quality gates', 'Schema evolution'],
  },
  {
    icon: '🤖',
    title: 'Agentic AI Platform',
    description:
      'Multi-agent systems that autonomously orchestrate pipelines, detect anomalies, generate insights, and govern ML models at enterprise scale.',
    points: ['Autonomous pipeline agents', 'Proactive anomaly detection', 'AI governance rails'],
  },
  {
    icon: '🔬',
    title: 'ML Lifecycle',
    description:
      'End-to-end model governance from experimentation to production - with 40–80% faster deployment cycles and full audit trails.',
    points: ['Experiment tracking', 'Model registry & versioning', 'CI/CD for ML'],
  },
  {
    icon: '🛡️',
    title: 'Data Governance',
    description:
      'Enterprise-grade lineage, cataloging, access controls, and compliance posture - built for HIPAA, SOX, GDPR, and GxP environments.',
    points: ['Automated data cataloging', 'Fine-grained access control', 'Compliance reporting'],
  },
];

const industries = [
  {
    icon: '🏦',
    industry: 'Financial Services',
    description:
      'Regulatory-compliant data platforms for risk analytics, fraud detection, and real-time trading intelligence.',
    outcomes: ['SOX & Basel III alignment', 'Sub-millisecond fraud signals', 'FINRA audit trails'],
  },
  {
    icon: '🏥',
    industry: 'Healthcare',
    description:
      'HIPAA-compliant Lakehouse architectures enabling clinical analytics, population health, and care optimization.',
    outcomes: ['HIPAA-native data controls', 'EHR integration patterns', 'Population health AI'],
  },
  {
    icon: '🧬',
    industry: 'Pharmaceuticals',
    description:
      'GxP-validated data environments for clinical trial management, compound discovery, and regulatory submissions.',
    outcomes: ['FDA 21 CFR Part 11 compliance', 'Clinical trial data lakes', 'R&D AI acceleration'],
  },
  {
    icon: '🛢️',
    industry: 'Petroleum',
    description:
      'Integrated data platforms for upstream, midstream, and downstream operations - enabling production analytics, asset monitoring, and ESG reporting.',
    outcomes: ['Real-time production analytics', 'Pipeline integrity monitoring', 'ESG emissions reporting'],
  },
];

export function HomePage() {
  return (
    <>
      <PageHead
        title="Home"
        description="NAATS transforms enterprise data environments into governed, AI-ready Medallion Lakehouses delivering 50-90% reliability improvements."
      />

      <HomepageHero />
      <StatsBar />

      {/* Platform Pillars */}
      <Section bg="page">
        <Container>
          <Stack gap="xl">
            <div className={styles.sectionHeader}>
              <p className={styles.eyebrow}>The NAATS Platform</p>
                <h2 className={styles.sectionTitle}>Four Integrated Capabilities.</h2>
                <h2 className={styles.sectionTitle}>One Unified Platform.</h2>
              <p className={styles.sectionDesc}>
                From raw data ingestion to autonomous AI agents - every layer governed, observable,
                and production-ready.
              </p>
            </div>
            <Grid cols={2} gap="md">
              {pillars.map((p) => (
                <PillarCard key={p.title} {...p} />
              ))}
            </Grid>
          </Stack>
        </Container>
      </Section>

      {/* Delivery Methodology */}
      <Section bg="subtle">
        <Container>
          <Stack gap="xl">
            <div className={styles.sectionHeader}>
              <p className={styles.eyebrow}>Our Methodology</p>
              <h2 className={styles.sectionTitle}>Assess. Architect. Accelerate.</h2>
              <p className={styles.sectionDesc}>
                A proven three-phase delivery model that gets you from fragmented data to enterprise
                AI - with measurable outcomes at every stage.
              </p>
            </div>
            <ProcessSteps />
          </Stack>
        </Container>
      </Section>

      {/* Industry Solutions */}
      <Section bg="page">
        <Container>
          <Stack gap="xl">
            <div className={styles.sectionHeader}>
              <p className={styles.eyebrow}>Industry Solutions</p>
              <h2 className={styles.sectionTitle}>Built for Regulated Industries</h2>
              <p className={styles.sectionDesc}>
                Compliance-native platforms purpose-built for the unique requirements of Financial
                Services, Healthcare, and Pharmaceuticals.
              </p>
            </div>
            <Grid cols={4} gap="md">
              {industries.map((ind) => (
                <IndustryCard key={ind.industry} {...ind} />
              ))}
            </Grid>
          </Stack>
        </Container>
      </Section>

      {/* Tech ecosystem */}
      <Section bg="subtle">
        <Container>
          <TechLogoGrid />
        </Container>
      </Section>

      <CTABand />
    </>
  );
}
